import {ButtonHTMLAttributes, FC, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import moment from 'moment'
import {ExportPdfProps} from '../../types'
import {jsPDF, jsPDFOptions} from 'jspdf'
import autoTable, {Styles} from 'jspdf-autotable'
import {getCSSVariableValue} from '_metronic/assets/ts/_utils'
import {useSelector} from 'react-redux'
import {RootState} from 'setup'
import {getTenantLogo, isDevEnv} from 'common/HelperFunctions'
import {GENERAL_ENABLE_DISABLE} from 'common/constants'
import {toast} from 'react-toastify'
import {useIntl} from 'react-intl'
import Spinner from 'common/components/Spinner'

type Props = {
  loading?: boolean
} & ButtonHTMLAttributes<HTMLButtonElement>

const PDFButton: FC<Props> = ({loading, ...props}) => {
  return (
    <OverlayTrigger overlay={<Tooltip>Download PDF</Tooltip>}>
      <button type='button' className='btn btn-sm btn-icon btn-light-danger m-1' {...props}>
        {loading ? <Spinner size='sm' /> : <i className='fa fa-file-pdf' />}
      </button>
    </OverlayTrigger>
  )
}

const colorToHex = (color: string): string => {
  const div = document.createElement('div')
  document.body.appendChild(div)

  div.style.color = color
  const computedColor = window.getComputedStyle(div).color
  document.body.removeChild(div)

  if (computedColor === '') {
    throw new Error('Invalid color format')
  }

  const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
  if (!rgbMatch) {
    throw new Error('Unable to parse computed color')
  }

  const [, r, g, b] = rgbMatch.map(Number)
  const toHex = (n: number): string => n.toString(16).padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

const ExportPdf: FC<ExportPdfProps> = ({columns, data, filename, title}) => {
  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings
  ) as any
  const intl = useIntl()
  const [loading, setLoading] = useState(false)

  if (data?.length === 0) return <PDFButton disabled />

  const downloadFileName = `${filename ?? 'PDF'}_${Date.now()}`

  const handlePrint = async () => {
    try {
      setLoading(true)
      const tableColumns = columns.filter(
        (column) => column.field !== 'action' && !column.hideInPDF
      )
      const tableRows: any[] = []

      for (let idx = 0; idx < data.length; idx++) {
        const row = data[idx]
        const object: any = {}

        for (let idx = 0; idx < tableColumns.length; idx++) {
          const columnHeading = tableColumns[idx]
          const key = columnHeading.field
          const column = columns.find((c) => c.field === key)

          if (!column) {
            continue
          }

          if (typeof column?.getText === 'function') {
            object[key] = column.getText(row)
          } else if (column?.type === 'date') {
            object[key] = moment(row[key]).format(
              ['h', 'm', 's', 'k', 'lll', 'lt', 'lts'].some((k) =>
                column.dateFormat?.toLocaleLowerCase()?.includes(k)
              )
                ? 'YYYY-MM-DD HH:mm:ss'
                : 'YYYY-MM-DD'
            )
          } else {
            object[key] = row[key]
          }
        }

        tableRows.push(object)
      }

      // Set dynamic column widths based on header and content length
      const columnWidths = tableColumns.map((column) => {
        const headerWidth = column.name.length * 2.5 // Dynamic width based on header length
        const maxDataLength = Math.max(...data.map((row) => String(row[column.field] || '').length)) // Find max data length for the column
        const dataWidth = maxDataLength * 2
        const calculatedWidth = Math.max(headerWidth, dataWidth, 30)
        return Math.min(calculatedWidth, 70)
      })

      const documentOptions: jsPDFOptions = {
        format: 'a4',
      }

      if (tableColumns.length > 5) {
        documentOptions.orientation = 'landscape'
      }

      const doc = new jsPDF(documentOptions)

      // Add the heading
      doc.setFontSize(14) // Font size for the title

      // Adding school logo
      const url = getTenantLogo('small')
      if (generalSettings?.hide_logo_reports === GENERAL_ENABLE_DISABLE.DISABLE && url) {
        const img = new Image()
        img.crossOrigin = 'anonymous'

        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
          img.src = `${url}?t=${Date.now()}`
        })

        const aspectRatio = img.width / img.height

        const maxWidth = 50
        const maxHeight = 20

        let finalWidth = maxWidth
        let finalHeight = finalWidth / aspectRatio

        if (finalHeight > maxHeight) {
          finalHeight = maxHeight
          finalWidth = finalHeight * aspectRatio
        }

        doc.addImage(img, 'png', 14, 2, finalWidth, finalHeight)
      }

      // Adding school name
      if (generalSettings?.tenantName) {
        const pageWidth = doc.internal.pageSize.getWidth()
        const textWidth = doc.getTextWidth(generalSettings.tenantName)
        const x = pageWidth - textWidth - 28
        doc.text(generalSettings.tenantName, x, 10)
      }

      if (title) {
        const pageWidth = doc.internal.pageSize.getWidth()
        const textWidth = doc.getTextWidth(title)
        const x = (pageWidth - textWidth) / 2
        doc.text(title, x, 35)
      }

      // Define the table headers
      const headers = [tableColumns.map((column) => column.name)]

      // Define the table data
      const tableData = tableRows.map((row) => tableColumns.map((column) => row[column.field]))

      // Starting position vertically
      const startY = 40

      // Calculate total width of columns
      const totalWidth = columnWidths.reduce((acc, width) => acc + width, 0)

      // If total width exceeds the page width, scale down the font size and column widths
      const pageWidth = doc.internal.pageSize.getWidth() - 40 // Subtract margins
      const scalingFactor = totalWidth > pageWidth ? pageWidth / totalWidth : 1

      // Generate the table
      autoTable(doc, {
        head: headers,
        body: tableData,
        startY: startY,
        theme: 'grid',
        styles: {
          fontSize: 11 * scalingFactor,
          cellPadding: 3,
          minCellHeight: 10,
          halign: 'center',
          valign: 'middle',
        },
        headStyles: {
          fillColor: colorToHex(getCSSVariableValue('--bs-primary-bg-subtle')),
          textColor: colorToHex(getCSSVariableValue('--bs-dark')),
        },
        columnStyles: columnWidths.reduce((styles, width, index) => {
          styles[index] = {
            cellWidth: width * scalingFactor,
            overflow: 'linebreak',
            valign: 'middle',
          } as Partial<Styles> // Ensure proper type
          return styles
        }, {} as Record<number, Partial<Styles>>),
        margin: {top: 30, left: 14, right: 14, bottom: 20},
      })

      doc.save(`${downloadFileName}.pdf`)
    } catch (err) {
      toast.error(intl.$t({id: 'GENERAL.SOMETHING_WENT_WRONG'}))
      if (isDevEnv()) {
        console.error(err)
      }
    } finally {
      setLoading(false)
    }
  }

  return <PDFButton loading={loading} onClick={handlePrint} disabled={loading} />
}

export default ExportPdf
